'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@clerk/nextjs';
import { ApiError } from '@lib/api/api-response';
import { useAuthedQuery } from '@lib/query/use-authed-query';
import type { InsightQueueRow } from '../types/insight-queue-row';
import { minutesUntilPipelineRedrive } from '../utils/pipeline-retry-schedule';
import { insightQueryKeys } from './insight-query-keys';

/** Poll cadence while a document is mid-pipeline. */
const ACTIVE_POLL_MS = 5000;

/**
 * Slower cadence while only failures remain. The rail still
 * has to advance its retry countdown and notice when the
 * sweeper re-drives, but neither needs five-second
 * resolution.
 */
const AWAITING_REDRIVE_POLL_MS = 30000;
async function fetchProcessingQueue({
  signal
}: {
  signal: AbortSignal;
}): Promise<InsightQueueRow[]> {
  // Forward the signal: a refetch TanStack supersedes must
  // actually abort, or the replaced request keeps running
  // server-side and stacks queue queries on the database.
  const response = await fetch('/api/protected/insights/queue', {
    signal
  });
  if (!response.ok) {
    throw new ApiError('Failed to fetch processing queue', response.status);
  }
  return response.json();
}
function pollIntervalFor(rows: InsightQueueRow[] | undefined): number | false {
  if (!rows?.length) return false;
  const midPipeline = rows.some(row => row.status === 'pending' || row.status === 'processing');
  if (midPipeline) return ACTIVE_POLL_MS;

  // A failure the sweeper will pick back up still changes
  // under the user: the countdown ticks down and the row
  // flips to processing without any action from them.
  const now = new Date();
  const awaitingRedrive = rows.some(row => row.status === 'failed' && minutesUntilPipelineRedrive(row, now) !== null);
  return awaitingRedrive ? AWAITING_REDRIVE_POLL_MS : false;
}
export function useInsightProcessingQueue() {
  const $ = _c(3);
  const {
    orgId
  } = useAuth();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = insightQueryKeys.processingQueue();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = !!orgId;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: fetchProcessingQueue,
      enabled: t1,
      refetchInterval: _temp
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useAuthedQuery(t2);
}
function _temp(query) {
  return pollIntervalFor(query.state.data);
}